import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../config';

import {
  Check,
  CircleNotch,
  FileText,
  MagnifyingGlass,
  X
} from '@phosphor-icons/react';
const AttachOpportunityModal = ({ isOpen, onClose, role, organizationId, onAttach }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && role) {
      fetchOpportunities();
    }
  }, [isOpen, role]);

  const fetchOpportunities = async () => {
    setLoading(true);
    try {
      const url = `${BACKEND_URL}/api/org/org-chart/role/${role.id}/opportunities?organization_id=${organizationId}`;
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOpportunities(data.opportunities || []);
      }
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOpportunity = (oppId) => {
    setSelectedOpportunities(prev => {
      if (prev.includes(oppId)) {
        return prev.filter(id => id !== oppId);
      }
      return [...prev, oppId];
    });
  };

  const handleAttach = () => {
    onAttach(selectedOpportunities);
    onClose();
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      opp.title?.toLowerCase().includes(searchLower) ||
      opp.description?.toLowerCase().includes(searchLower) ||
      opp.category?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className="glass rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              Attach Opportunities
            </h2>
            <p className="text-[#A0AEC0] text-sm">
              Select opportunities for: <span className="text-white font-semibold">{role?.title}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={20} className="text-[#A0AEC0]" />
          </button>
        </div>

        {/* MagnifyingGlass */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#A0AEC0]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search opportunities..."
              className="w-full pl-12 pr-4 py-3 rounded-xl glass text-white outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
          </div>
        </div>

        {/* Opportunities List */}
        <div className="flex-1 overflow-y-auto space-y-3 mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CircleNotch size={32} className="text-[#6366F1] animate-spin" />
            </div>
          ) : filteredOpportunities.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-[#718096] mb-4" />
              <p className="text-[#A0AEC0]">
                {searchTerm ? 'No opportunities match your search' : 'No opportunities found for this role'}
              </p>
            </div>
          ) : (
            filteredOpportunities.map((opp) => {
              const isSelected = selectedOpportunities.includes(opp.opportunity_id);
              return (
                <div
                  key={opp.opportunity_id}
                  onClick={() => toggleOpportunity(opp.opportunity_id)}
                  className={`glass rounded-xl p-4 cursor-pointer transition-all ${
                    isSelected
                      ? 'ring-2 ring-[#6366F1] bg-[rgba(99,102,241,0.1)]'
                      : 'hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected
                          ? 'border-[#6366F1] bg-[#6366F1]'
                          : 'border-[#718096]'
                      }`}
                    >
                      {isSelected && <Check size={16} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">{opp.title}</h3>
                      {opp.description && (
                        <p className="text-[#A0AEC0] text-sm line-clamp-2 mb-2">
                          {opp.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs">
                        {opp.category && (
                          <span className="px-2 py-1 rounded-full bg-[#6366F1]/20 text-[#6366F1]">
                            {opp.category}
                          </span>
                        )}
                        {opp.priority_level && (
                          <span
                            className={`px-2 py-1 rounded-full ${
                              opp.priority_level === 'high'
                                ? 'bg-[#EF4444]/20 text-[#EF4444]'
                                : opp.priority_level === 'medium'
                                ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                                : 'bg-[#10B981]/20 text-[#10B981]'
                            }`}
                          >
                            {opp.priority_level} priority
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleAttach}
            disabled={selectedOpportunities.length === 0}
            className="flex-1 relative overflow-hidden rounded-full group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
            <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
              Attach {selectedOpportunities.length > 0 && `(${selectedOpportunities.length})`}
            </span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default AttachOpportunityModal;
